/**
 * PersonDetailPage — person detail (henkilörekisteri, #763/#764)
 *
 * Shows contact info, the companies the person is linked to, the vehicles they
 * are an active party to and the work orders they have ordered (#2519).
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { usePerson, useDeletePerson } from '@/hooks/usePersons';
import { AttachmentsCard } from '@/components/attachments/AttachmentsCard';
import { CustomerEmailDeliverySection } from '@/components/email/CustomerEmailDeliverySection';
import { PartyVehiclesSection } from '@/components/parties/PartyVehiclesSection';
import { PartyWorkOrdersSection } from '@/components/parties/PartyWorkOrdersSection';
import { PersonCompanyLinksSection } from '@/components/persons/PersonCompanyLinksSection';
import { PersonFormDialog } from '@/components/persons/PersonFormDialog';
import {
  ConfirmDialog,
  DetailField,
  DetailPageHeader,
  DetailSection,
  PermissionGuard,
} from '@/components/common';
import { useNotify } from '@/stores/notificationStore';

export default function PersonDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('persons');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const notify = useNotify();

  const { data: person, isLoading } = usePerson(id);
  const deleteMutation = useDeletePerson();

  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!person) {
    return <Typography color="text.secondary">{t('empty')}</Typography>;
  }

  const fullName = `${person.firstName} ${person.lastName}`;

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(person.id);
      notify.success(t('deleted'), '');
      navigate('/parties');
    } catch {
      notify.error(tc('error'), '');
    }
  };

  return (
    <Box>
      <DetailPageHeader
        title={fullName}
        onBack={() => navigate('/parties')}
        onEdit={() => setEditOpen(true)}
        onDelete={() => setConfirmDelete(true)}
      />

      <Stack spacing={3}>
        <DetailSection title={t('detail.contactInfo')}>
          <Stack spacing={2}>
            <DetailField label={t('fields.phone')} value={person.phone} />
            <DetailField label={t('fields.email')} value={person.email} />
            <DetailField label={t('fields.invoiceEmail')} value={person.invoiceEmail} />
            <DetailField label={t('fields.whatsappNumber')} value={person.whatsappNumber} />
            <DetailField label={t('fields.preferredLanguage')} value={person.preferredLanguage} />
            <DetailField label={t('fields.streetAddress')} value={person.streetAddress} />
            <DetailField label={t('fields.postalCode')} value={person.postalCode} />
            <DetailField label={t('fields.city')} value={person.city} />
            <DetailField label={t('fields.notes')} value={person.notes} />
          </Stack>
        </DetailSection>

        <PersonCompanyLinksSection personId={person.id} />

        <PermissionGuard permission="vehicles.read">
          <PartyVehiclesSection personId={person.id} />
        </PermissionGuard>

        <PermissionGuard permission="work_orders.read">
          <PartyWorkOrdersSection personId={person.id} />
        </PermissionGuard>

        <CustomerEmailDeliverySection customerType="person" customerId={person.id} />

        <AttachmentsCard
          entityLinks={[{ entityType: 'person', entityId: person.id }]}
          canModify={() => true}
          defaultTags={[fullName]}
        />
      </Stack>

      <PersonFormDialog open={editOpen} person={person} onClose={() => setEditOpen(false)} />
      <ConfirmDialog
        open={confirmDelete}
        title={t('deleteConfirmTitle')}
        message={t('deleteConfirmMessage', { name: fullName })}
        confirmText={tc('delete')}
        confirmColor="error"
        loading={deleteMutation.isPending}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}
